import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  Heading,
  SimpleGrid,
  Spinner,
  Text,
  Alert,
  AlertIcon,
  IconButton,
  Flex,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useBreakpointValue,
  useToast,
} from '@chakra-ui/react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowBackIcon } from '@chakra-ui/icons';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import VehicleDetailsCard from '../Components/CarInfoCards/VehicleDetailsCard';
import OwnerSummaryCard from '../Components/CarInfoCards/OwnerSummaryCard';
import OwnerHistoryCard from '../Components/CarInfoCards/OwnerHistoryCard';
import VehiclePricingCard from '../Components/CarInfoCards/VehiclePricingCard';
import MaintenanceLogsCard from '../Components/CarInfoCards/MaintenanceLogsCard';
import ExpertChatLogsCard from '../Components/CarInfoCards/ExpertChatLogsCard';
import { useGetVehicle, useGetVehicleScoreAndValuation } from '../utils/vehicles.api';
import VehicleDetailsForm from '../Components/VehicleDetailsForm';
import { useUIStore } from '../store/uiStore';
import { useAuthStore } from '../store/authStore';
import { useChatStore } from '../store/chatStore';
import { useVehicleStore } from '../store/vehicleStore';
import ChatInputSection from '../Components/ChatInputSection';
import {
  useCreateConversation,
  useSendImageMessage,
  useSendMessage,
  useSendVoiceMessage,
} from '../utils/conversation.api';

const HEADER_HEIGHT = 80;

const CarInfo = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const vehicleId = searchParams.get('vehicleId');
  
  // Zustand stores
  const {
    isSidebarOpen,
    isMobileDrawerOpen,
    isVehicleFormOpen,
    vehicleToEdit,
    toggleSidebar,
    toggleMobileDrawer,
    openVehicleForm,
    closeVehicleForm,
    setVehicleToEdit,
    setIsChatView,
  } = useUIStore();
  
  const { initializeAuth } = useAuthStore();
  
  const isLoggedIn = !!localStorage.getItem('token');
  const toast = useToast();

  // Chat input states
  const [newMessage, setNewMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedAudio, setSelectedAudio] = useState(null);
  const [isDiagnoseMode, setIsDiagnoseMode] = useState(false);
  const [isBuySellMode, setIsBuySellMode] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);

  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const textareaRef = useRef(null);
  const { clearChatMessages } = useChatStore();
  const { setSelectedVehicle, setSelectedVehicleConversationId } = useVehicleStore();

  const createConversationMutation = useCreateConversation();
  const { mutateAsync: sendTextMessage } = useSendMessage();
  const { mutateAsync: sendImageMessage } = useSendImageMessage();
  const { mutateAsync: sendVoiceMessage } = useSendVoiceMessage();

  // Responsive behavior - drawer on mobile, sidebar on desktop
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const showSidebar = !isMobile && isSidebarOpen;
  const showDrawer = isMobile && isMobileDrawerOpen;
  const [chatBottomPadding, setChatBottomPadding] = useState('360px');
  const chatSectionRef = useRef(null);

  useEffect(() => {
    const node = chatSectionRef.current;
    if (!node) return;

    const updatePadding = () => {
      const height = node.offsetHeight || 0;
      setChatBottomPadding(`${height + 40}px`);
    };

    updatePadding();

    const resizeObserver = new ResizeObserver(updatePadding);
    resizeObserver.observe(node);

    window.addEventListener('resize', updatePadding);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updatePadding);
    };
  }, [isMobile, showSidebar]);


  // Initialize auth on mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Handle vehicle selection - navigate to carinfo with selected vehicle
  const handleVehicleSelect = (vehicle) => {
    if (vehicle && vehicle.id) {
      navigate(`/carinfo?vehicleId=${vehicle.id}`);
      // Close mobile drawer if open
      if (showDrawer) {
        toggleMobileDrawer();
      }
    }
  };

  // Handle clear chat (not used in CarInfo but required by Sidebar)
  const handleClearChat = () => {
    // Not applicable for CarInfo page
  };

  // Handle settings click
  const handleSettingsClick = () => {
    navigate('/dashboard/settings');
  };

  const handleDiagnoseRepairVehicle = () => {
    setIsDiagnoseMode((prev) => !prev);
    setIsBuySellMode(false);
    toast({
      title: !isDiagnoseMode ? 'Diagnose mode enabled' : 'Diagnose mode disabled',
      status: 'info',
      duration: 2000,
      isClosable: true,
      position: 'top-right',
    });
  };

  const handleBuySell = () => {
    setIsBuySellMode((prev) => !prev);
    setIsDiagnoseMode(false);
    toast({
      title: !isBuySellMode ? 'Vehicle value mode enabled' : 'Vehicle value mode disabled',
      status: 'info',
      duration: 2000,
      isClosable: true,
      position: 'top-right',
    });
  };

  const handleGoToGarage = () => {
    navigate('/dashboard');
  };

  const handleHelpExpert = () => {
    toast({
      title: 'Expert assistance',
      description: 'Please use the main chat to speak with an expert.',
      status: 'info',
      duration: 2500,
      isClosable: true,
      position: 'top-right',
    });
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleCarIconClick = () => {
    audioInputRef.current?.click();
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedImageTypes.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: 'Please select a JPG, JPEG, or PNG image file',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      e.target.value = ''; // Reset input to allow selecting the same file again
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please select an image smaller than 10MB',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      e.target.value = ''; // Reset input to allow selecting the same file again
      return;
    }

    setSelectedImage(file);
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const handleAudioSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 25 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please select an audio file smaller than 25MB',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      e.target.value = ''; // Reset input to allow selecting the same file again
      return;
    }

    setSelectedAudio(file);
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const handleTextareaChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    // Only adjust height for real DOM events (not synthetic events from suggestions)
    if (e.target && e.target.tagName === 'TEXTAREA') {
      setTimeout(() => {
        const textarea = textareaRef.current;
        // Get the actual DOM element (Chakra UI might wrap it)
        const textareaElement = textarea?.querySelector?.('textarea') || textarea;
        // Only adjust height if we have a valid DOM element with style property
        if (textareaElement && textareaElement.style) {
          textareaElement.style.height = 'auto';
          const newHeight = Math.min(textareaElement.scrollHeight || 60, 120);
          textareaElement.style.height = `${newHeight}px`;
        }
      }, 10);
    }
  };

  const resetTextareaHeight = () => {
    const textarea = textareaRef.current;
    const textareaElement = textarea?.querySelector?.('textarea') || textarea;
    if (textareaElement && textareaElement.style) {
      textareaElement.style.height = '40px';
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !selectedImage && !selectedAudio) {
      toast({
        title: 'Message required',
        description: 'Type a question or attach media to continue.',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    if (!isLoggedIn) {
      toast({
        title: 'Login required',
        description: 'Please sign in to continue the conversation.',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    if (isSending || isVehicleRecordLoading) {
      return;
    }

    try {
      setIsSending(true);

      const conversationId = await ensureConversationId();
      const pendingRequests = [];
      const trimmedMessage = newMessage.trim();

      if (trimmedMessage) {
        pendingRequests.push(
          sendTextMessage({
            conversationId,
            messageData: {
              message: trimmedMessage,
              message_type: determineMessageType(),
              metadata: null,
              user_id: null,
            },
          })
        );
      }

      if (selectedImage) {
        pendingRequests.push(
          sendImageMessage({
            conversationId,
            imageFile: selectedImage,
            prompt:
              trimmedMessage ||
              'Please analyze this vehicle image and provide guidance.',
          })
        );
      }

      if (selectedAudio) {
        pendingRequests.push(
          sendVoiceMessage({
            conversationId,
            voiceFile: selectedAudio,
            additionalData: {
              prompt:
                trimmedMessage ||
                'Please analyze this audio and help diagnose the issue.',
            },
          })
        );
      }

      await Promise.all(pendingRequests);

      const normalizedVehicle = {
        ...(vehicleRecord || {}),
        id: vehicleRecord?.id || vehicleId,
        conversation_id: conversationId,
      };

      setSelectedVehicle(normalizedVehicle);
      setSelectedVehicleConversationId(conversationId);
      clearChatMessages();
      setIsChatView(true);

      toast({
        title: 'Message sent',
        description: 'Redirecting you to the chat view.',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });

      setTimeout(() => {
        navigate(`/dashboard?vehicleId=${vehicleId}`);
      }, 300);
    } catch (err) {
      console.error('Failed to send message from CarInfo:', err);
      toast({
        title: 'Unable to send message',
        description: err?.message || 'Please try again in a moment.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSending(false);
      setNewMessage('');
      setSelectedImage(null);
      setSelectedAudio(null);
      resetTextareaHeight();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const renderChatInputSection = () => (
    <ChatInputSection
      ref={chatSectionRef}
      isMobile={!!isMobile}
      isLoggedIn={isLoggedIn}
      isDiagnoseMode={isDiagnoseMode}
      isBuySellMode={isBuySellMode}
      onDiagnoseRepairVehicle={handleDiagnoseRepairVehicle}
      onBuySell={handleBuySell}
      onGoToGarage={handleGoToGarage}
      onHelpExpert={handleHelpExpert}
      goToGarageLabel="Go to Chat"
      newMessage={newMessage}
      selectedImage={selectedImage}
      selectedAudio={selectedAudio}
      isLoading={isSending || isVehicleRecordLoading}
      onMessageChange={handleTextareaChange}
      onKeyPress={handleKeyPress}
      onSendMessage={handleSendMessage}
      onImageButtonClick={handleImageButtonClick}
      onCarIconClick={handleCarIconClick}
      onSidebarToggle={handleMenuClick}
      onRemoveImage={() => setSelectedImage(null)}
      onRemoveAudio={() => setSelectedAudio(null)}
      fileInputRef={fileInputRef}
      audioInputRef={audioInputRef}
      textareaRef={textareaRef}
      handleImageSelect={handleImageSelect}
      handleAudioSelect={handleAudioSelect}
    />
  );

  // Call the API with the vehicle ID
  const {
    data: vehicleData,
    isLoading,
    error,
    refetch: refetchValuation,
    isRefetching,
  } = useGetVehicleScoreAndValuation(vehicleId);

  const {
    data: vehicleRecord,
    isLoading: isVehicleRecordLoading,
    error: vehicleRecordError,
  } = useGetVehicle(vehicleId);

  useEffect(() => {
    if (vehicleRecord?.conversation_id) {
      setActiveConversationId(vehicleRecord.conversation_id);
    }
  }, [vehicleRecord]);

  useEffect(() => {
    if (vehicleRecordError) {
      console.error('Failed to load vehicle details for chat context:', vehicleRecordError);
    }
  }, [vehicleRecordError]);

  const determineMessageType = () => {
    if (isDiagnoseMode) return 'repair';
    if (isBuySellMode) return 'sell';
    return 'text';
  };

  const ensureConversationId = useCallback(async () => {
    if (activeConversationId) {
      return activeConversationId;
    }

    const conversationName =
      vehicleData?.vehicle_info?.model ||
      vehicleRecord?.model ||
      `Vehicle ${vehicleId}`;

    const conversationPayload = {
      conversation_name: conversationName,
      vehicle_id: vehicleId,
      conversation_type: 'vehicle_specific',
      user_id: null,
    };

    const response = await createConversationMutation.mutateAsync(
      conversationPayload
    );

    if (!response?.id) {
      throw new Error('Unable to create conversation for this vehicle.');
    }

    setActiveConversationId(response.id);
    return response.id;
  }, [
    activeConversationId,
    createConversationMutation,
    vehicleData?.vehicle_info?.model,
    vehicleRecord?.model,
    vehicleId,
  ]);


  // Handle menu click based on device type
  const handleMenuClick = () => {
    if (isMobile) {
      toggleMobileDrawer();
    } else {
      toggleSidebar();
    }
  };


  // Handle back navigation
  const handleBackClick = () => {
    navigate(-1); // Go back to previous page in history
  };

  const renderPageContent = () => {
    if (!vehicleId) {
      return (
        <>
          <Flex align="center" mb={4}>
            <IconButton
              icon={<ArrowBackIcon />}
              aria-label="Go back"
              variant="ghost"
              size="md"
              onClick={handleBackClick}
              mr={{ base: 2, md: 4 }}
              _hover={{ bg: 'gray.100' }}
            />
            <Text fontSize={{ base: "md", md: "lg" }} fontWeight="medium" color="gray.700">
              Vehicle Information
            </Text>
          </Flex>
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <Text>
              No vehicle ID provided. Please select a vehicle from the sidebar.
            </Text>
          </Alert>
        </>
      );
    }

    if (isLoading) {
      return (
        <>
          <Flex align="center" mb={4}>
            <IconButton
              icon={<ArrowBackIcon />}
              aria-label="Go back"
              variant="ghost"
              size="md"
              onClick={handleBackClick}
              mr={{ base: 2, md: 4 }}
              _hover={{ bg: 'gray.100' }}
            />
            <Text fontSize={{ base: "md", md: "lg" }} fontWeight="medium" color="gray.700">
              Vehicle Information
            </Text>
          </Flex>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            minH="60vh"
          >
            <Box textAlign="center">
              <Spinner size="xl" color="blue.500" mb={4} />
              <Text fontSize="lg" color="gray.600">
                Loading vehicle information...
              </Text>
            </Box>
          </Box>
        </>
      );
    }

    if (error) {
      return (
        <>
          <Flex align="center" mb={4}>
            <IconButton
              icon={<ArrowBackIcon />}
              aria-label="Go back"
              variant="ghost"
              size="md"
              onClick={handleBackClick}
              mr={{ base: 2, md: 4 }}
              _hover={{ bg: 'gray.100' }}
            />
            <Text fontSize={{ base: "md", md: "lg" }} fontWeight="medium" color="gray.700">
              Vehicle Information
            </Text>
          </Flex>
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Text>Failed to load vehicle information: {error.message}</Text>
          </Alert>
        </>
      );
    }

    return (
      <>
        <Flex align="center" mb={8}>
          <IconButton
            icon={<ArrowBackIcon />}
            aria-label="Go back"
            variant="ghost"
            size="lg"
            onClick={handleBackClick}
            mr={{ base: 2, md: 4 }}
            _hover={{ bg: 'gray.100' }}
          />
          <Heading 
            textAlign="center" 
            color="#212e89" 
            flex={1}
            fontSize={{ base: "lg", md: "3xl" }}
            lineHeight={{ base: "1.3", md: "1.2" }}
            fontWeight="bold"

          >
            Vehicle Information Dashboard 
          </Heading>
        </Flex>

        {/* Row 1 */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
          <VehicleDetailsCard vehicleData={vehicleData} />

            <OwnerHistoryCard vehicleData={vehicleData} />

          {/* <VehiclePricingCard 
            vehicleData={vehicleData} 
            onRecalculate={refetchValuation}
            isRecalculating={isRefetching}
          /> */}
        </SimpleGrid>

        {/* Row 2 */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={6}>
          {/* <OwnerHistoryCard vehicleData={vehicleData} /> */}
          <VehiclePricingCard 
            vehicleData={vehicleData} 
            onRecalculate={refetchValuation}
            isRecalculating={isRefetching}
          />
          <OwnerSummaryCard
            vehicleData={vehicleData}
            aiScore={vehicleData?.ai_score || 0}
          />
        </SimpleGrid>

        {/* Row 3 - Maintenance Logs and Expert Chat Logs */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}  pb={{ base: `max(${chatBottomPadding}, 180px)`, md: `max(${chatBottomPadding}, 180px)` }} >
          <MaintenanceLogsCard vehicleId={vehicleId} />
          <ExpertChatLogsCard vehicleId={vehicleId} />
        </SimpleGrid>
      </>
    );
  };

  return (
    <Box bg="white" minH="100vh" display="flex" flexDirection="column" >
      <Box
        position="sticky"
        top="0"
        zIndex="1000"
        bg="white"
        boxShadow="sm"
      >
        <Header
          isMobile={isMobile}
          onMenuClick={handleMenuClick}
          isChatView={false}
        />
      </Box>
      <Flex flex="1" overflow="hidden">
        {/* Sidebar - Desktop */}
        {showSidebar && (
          <Box
            as="aside"
            w={{ base: 0, lg: '320px' }}
            maxW="360px"
            borderRight="1px solid"
            borderColor="gray.100"
            bg="white"
            display={{ base: 'none', lg: 'block' }}
          >
            <Box 
              h={`calc(100vh - ${HEADER_HEIGHT}px)`} 
              overflowY="auto"
              sx={{
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#212e89',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#1a2369',
                },
                scrollbarWidth: 'thin',
                scrollbarColor: '#212e89 #f1f1f1',
              }}
            >
              <Sidebar
                isOpen={showSidebar}
                isMobile={isMobile}
                onSettingsClick={handleSettingsClick}
                onVehicleSelect={handleVehicleSelect}
                onClearChat={handleClearChat}
              />
            </Box>
          </Box>
        )}

        {/* Main Content */}
        <Box flex={1} overflow="hidden">
          <Box
            p={{ base: 4, md: 8 }}
            bg="gray.50"
            minH="100%"
            h={`calc(100vh - ${HEADER_HEIGHT}px)`}
            overflowY="auto"
            pb={chatBottomPadding}
            scrollPaddingBottom={chatBottomPadding}
            sx={{
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#212e89',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#1a2369',
              },
              scrollbarWidth: 'thin',
              scrollbarColor: '#212e89 #f1f1f1',
            }}
          >
            {renderPageContent()}
          </Box>
        </Box>
      </Flex>

      {/* Mobile Drawer */}
      <Drawer isOpen={showDrawer} placement="left" onClose={toggleMobileDrawer}>
        <DrawerOverlay />
        <DrawerContent maxW="70%">
          <DrawerCloseButton />
          <Box 
            h="100%" 
            overflowY="auto"
            sx={{
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#212e89',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#1a2369',
              },
              scrollbarWidth: 'thin',
              scrollbarColor: '#212e89 #f1f1f1',
            }}
          >
            <Sidebar
              isOpen={true}
              isMobile={true}
              onSettingsClick={handleSettingsClick}
              onVehicleSelect={handleVehicleSelect}
              onClearChat={handleClearChat}
            />
          </Box>
        </DrawerContent>
      </Drawer>

      {/* Vehicle Details Form Modal */}
      <VehicleDetailsForm
        isOpen={isVehicleFormOpen}
        onClose={() => {
          closeVehicleForm();
          setVehicleToEdit(null);
        }}
        vehicleToEdit={vehicleToEdit}
        onEditComplete={() => {
          setVehicleToEdit(null);
        }}
      />
      {renderChatInputSection()}
    </Box>
  );
};

export default CarInfo;
